import { useCallback } from "react";
import {
  useAppDispatch,
  useAppSelector,
} from "../../../presentation/hooks/store-hooks";
import { useGeolocalizacaoApi } from "../../../infra/geo-veles";
import {
  setGeolocalizacaoOpenState,
  setGeolocalizacaoRadiusState,
  setGeolocalizacoesEncontradasState,
  setOpenGeolocalizacaoDialogState,
  setResetGeolocalizacoesEncontradasState,
  setResetSelectedGeolocalizacaoState,
  setSearchTermState,
  setSelectedGeolocalizacaoState,
  setResetSearchTermState,
  setResetRadiusState,
  
} from "../../../application/states/geo-veles";
import { IGeolocalizacao } from "../../../application/models";
import { useUIService } from "../../ui";
import { useProjetosService } from "../projetos";

export const useGeolocalizacaoService = () => {
  // Dispatch
  const dispatch = useAppDispatch();
  // State
  const {
    searchTerm,
    selectedGeolocalizacao,    
    radius,
  } = useAppSelector((state) => state.geolocalizacao);
  // Hook
  const { setAlert, setLoading } = useUIService();
  const { setGeolocalizacaoFilter } = useProjetosService();
  // API
  const {
    getGeolocalizacaoBySearchTermApi,
    getGeolocalizacaoDetailsApi,
    getGeolocalizacaoByPlaceIdApi,
    getGeolocalizacaoByLatLonApi,
  } = useGeolocalizacaoApi();

  const setSearchTerm = useCallback(
    (data: { searchTerm: string }) => {
      const normalizeText = data.searchTerm.replace("  ", " ");

      dispatch(
        setSearchTermState({
          searchTerm: normalizeText,
        })
      );
    },
    [dispatch]
  );

  const setSelectedGeolocalizacao = useCallback(
    (data: { geolocalizacao: IGeolocalizacao }) => {
      dispatch(
        setSelectedGeolocalizacaoState({
          geolocalizacao: data.geolocalizacao,
        })
      );

      setGeolocalizacaoFilter({
        geolocalizacao: {
          data: data.geolocalizacao,
          radius: radius,
        },
      });
    },
    [dispatch, radius, setGeolocalizacaoFilter]
  );

  const setGeolocalizacoesEncontradas = useCallback(
    (data: { geolocalizacoes: IGeolocalizacao[] }) => {
      dispatch(
        setGeolocalizacoesEncontradasState({
          geolocalizacoes: data.geolocalizacoes,
        })
      );
    },
    [dispatch]
  );

  const resetSelectedGeolocalizacao = useCallback(() => {
    dispatch(setResetSelectedGeolocalizacaoState());
  }, [dispatch]);

  const resetGeolocalizacoesEncontradas = useCallback(() => {
    dispatch(setResetGeolocalizacoesEncontradasState());
  }, [dispatch]);

  const getGeolocalizacao = useCallback(() => {
    if (searchTerm.length === 0) {
      setAlert({
        message: "O campo de pesquisa não pode estar em branco",
        type: "error",
      });

      return;
    }

    const loadingId = Math.random().toString();

    setLoading({
      id: loadingId,
      loading: true,
      message: "Buscando...",
    });

    getGeolocalizacaoBySearchTermApi({ searchTerm: searchTerm.toLowerCase() })
      .then(async (result: any) => {
        const geolocalizacoesEncontradas = result.geolocalizacoes;

        if (geolocalizacoesEncontradas.length === 0) {
          setAlert({
            message: "Nenhuma localização encontrada",
            type: "error",
          });
          return;
        }

        let geolocalizacoes: IGeolocalizacao[] = [];

        for (let i = 0; i < geolocalizacoesEncontradas.length; i++) {
          const geolocalizacao = geolocalizacoesEncontradas[i];

          if (
            geolocalizacao.address.country !== "Brasil" &&
            geolocalizacao.address.country !== "Brazil"
          ) {
            continue;
          }

          let addressRank: number = 0;
          let geolocalizacaoDetails = undefined;
          let coordinates = [];

          if (geolocalizacao.type === "postcode") {
            //dispatch(setResetGeolocalizacoesEncontradasState())
            geolocalizacaoDetails = await getGeolocalizacaoByPlaceIdApi({
              placeId: Number(geolocalizacao.place_id),
            });
          } else {
            geolocalizacaoDetails = await getGeolocalizacaoDetailsApi({
              osmType: String(geolocalizacao.osm_type[0]).toUpperCase(),
              osmId: Number(geolocalizacao.osm_id),
              class: String(geolocalizacao.class),
            });
          }

          if (geolocalizacaoDetails)
            addressRank = geolocalizacaoDetails.geolocalizacao.rank_address;

          if (geolocalizacao.addresstype === "country") {
            for (
              let c = 0;
              c < geolocalizacao.geojson.coordinates.length;
              c++
            ) {
              const coordinate = geolocalizacao.geojson.coordinates[c];

              for (let k = 0; k < coordinate.length; k++) {
                const coordinateItemArray = coordinate[k];

                coordinates.push(coordinateItemArray);
              }
            }
          } else if (geolocalizacao.geojson.coordinates.length === 1) {
            coordinates = geolocalizacao.geojson.coordinates[0];
          } else {
            coordinates = geolocalizacao.geojson.coordinates;
          }     

          const newGeolocalizacao: IGeolocalizacao = {
            address: {
              city: geolocalizacao.address.city,
              country: geolocalizacao.address.country,
              county: geolocalizacao.address.county,
              municipality: geolocalizacao.address.municipality,
              postcode: geolocalizacao.address.postcode,
              quarter: geolocalizacao.address.quarter,
              railway: geolocalizacao.address.railway,
              region: geolocalizacao.address.region,
              road: geolocalizacao.address.road,
              state: geolocalizacao.address.state,
              stateDistrict: geolocalizacao.address.state_district,
              suburb: geolocalizacao.address.suburb,
            },
            latLon: {
              coordinates: coordinates,
              type: geolocalizacao.geojson.type,
            },
            lat: Number(geolocalizacao.lat),
            lon: Number(geolocalizacao.lon),
            name: geolocalizacao.display_name,
            placeRank: addressRank,
            type: geolocalizacao.type,
          };
          geolocalizacoes.push(newGeolocalizacao);
        }

        if (geolocalizacoes.length === 0) {
          setAlert({
            message: "Nenhuma localização encontrada",
            type: "error",
          });
          return;
        }

        dispatch(setGeolocalizacoesEncontradasState({ geolocalizacoes }));
        dispatch(
          setGeolocalizacaoOpenState({
            open: true,
          })
        );
      })
      .catch((error) => {
        if(error.response.status === 404){
            error.message = 'Localização não encontrada'
        }
        setAlert({
          message: error.message,
          type: "error",
        });
      })
      .finally(() => {
        setLoading({
          id: loadingId,
          loading: false,
        });
      });
  }, [
        searchTerm,
        setLoading,
        getGeolocalizacaoBySearchTermApi,
        setAlert,
        dispatch,
        getGeolocalizacaoByPlaceIdApi,
        getGeolocalizacaoDetailsApi,
    ]);

  const setGeolocalizacaoRadius = useCallback(
    (data: { radius: number }) => {
      dispatch(
        setGeolocalizacaoRadiusState({
          radius: data.radius,
        })
      );

      // setGeolocalizacaoFilterRadius({
      //     radius: data.radius
      // })
    },
    [dispatch]
  );

  const setGeolocalizacaoOpen = useCallback(
    (data: { open: boolean }) => {
      dispatch(
        setGeolocalizacaoOpenState({
          open: data.open,
        })
      );
    },
    [dispatch]
  );

  const setResetSelectedGeolocalizacao = useCallback(() => {
    dispatch(setResetSelectedGeolocalizacaoState());
  }, [dispatch]);

  const getGeolocalizacaoByLatLon = useCallback(
    (data: { lat: number; lon: number }) => {
      let newSelectedGeolocalizacao: IGeolocalizacao | undefined =
        selectedGeolocalizacao;

      const loadingId = Math.random().toString();

      setLoading({
        id: loadingId,
        loading: true,
      });

      getGeolocalizacaoByLatLonApi({
        lat: data.lat,
        lon: data.lon,
      })
        .then((response) => {
          if (response) {
            const { geolocalizacao } = response;

            if (
              (geolocalizacao.address.country !== "Brasil" &&
                geolocalizacao.address.country !== "Brazil") ||
              !geolocalizacao.address.country
            ) {
              setAlert({
                message: "Selecione algum território brasileiro",
                type: "error",
              });

              if (selectedGeolocalizacao) {
                newSelectedGeolocalizacao = selectedGeolocalizacao;
              }
            } else {
              const newGeolocalizacao: IGeolocalizacao = {
                address: {
                  city: geolocalizacao.address.city,
                  country: geolocalizacao.address.country,
                  county: geolocalizacao.address.county,
                  municipality: geolocalizacao.address.municipality,
                  postcode: geolocalizacao.address.postcode,
                  quarter: geolocalizacao.address.quarter,
                  railway: geolocalizacao.address.railway,
                  region: geolocalizacao.address.region,
                  road: geolocalizacao.address.road,
                  state: geolocalizacao.address.state,
                  stateDistrict: geolocalizacao.address.state_district,
                  suburb: geolocalizacao.address.suburb,
                },
                latLon: {
                  coordinates: [
                    [Number(geolocalizacao.lat), Number(geolocalizacao.lon)],
                  ],
                  type: "Point",
                },
                lat: Number(geolocalizacao.lat),
                lon: Number(geolocalizacao.lon),
                name: geolocalizacao.display_name,
                placeRank: 28,
                type: geolocalizacao.type,
              };

              newSelectedGeolocalizacao = newGeolocalizacao;
            }
          }
        })
        .catch((error) => {
          setAlert({
            message: "Não é possível selecionar esta localização",
            type: "error",
          });

          if (selectedGeolocalizacao) {
            newSelectedGeolocalizacao = selectedGeolocalizacao;
          }
        })
        .finally(() => {
          dispatch(setResetSelectedGeolocalizacaoState());

          if (newSelectedGeolocalizacao) {
            dispatch(
              setSelectedGeolocalizacaoState({
                geolocalizacao: newSelectedGeolocalizacao,
              })
            );
            setGeolocalizacaoFilter({
              geolocalizacao: {
                data: newSelectedGeolocalizacao,
                radius: radius,
              },
            });
          }
          setLoading({
            id: loadingId,
            loading: false,
          });
        });
    },
    [
      dispatch,
      getGeolocalizacaoByLatLonApi,
      radius,
      selectedGeolocalizacao,
      setAlert,
      setGeolocalizacaoFilter,
      setLoading,
    ]
  );

  const openGeolocalizacaoDialog = useCallback(
    (openDialog: boolean) => {
      dispatch(setOpenGeolocalizacaoDialogState(openDialog));
    },
    [dispatch]
  );

  const resetSearchTerm = () => {
    dispatch(setResetSearchTermState())
  }

  const resetRadius = () => {
    dispatch(setResetRadiusState())
  }

  return {
    setSearchTerm,
    setSelectedGeolocalizacao,
    setGeolocalizacoesEncontradas,
    getGeolocalizacao,
    setGeolocalizacaoOpen,
    setGeolocalizacaoRadius,
    setResetSelectedGeolocalizacao,
    getGeolocalizacaoByLatLon,
    openGeolocalizacaoDialog,
    resetSelectedGeolocalizacao,
    resetGeolocalizacoesEncontradas,
    resetSearchTerm,
    resetRadius,
  };
};
